import React, { useEffect, useRef, useCallback } from "react";

export function useShadowDomOutsideClick<T extends HTMLElement>(
  handler: (event: MouseEvent | TouchEvent) => void,
  listenCapturing: boolean = true,
): React.RefObject<T> {
  const ref = useRef<T>(null);

  const handleClickOutside = useCallback(
    (event: MouseEvent | TouchEvent) => {
      const targetElement = ref.current;

      const isInside = (target: EventTarget | null): boolean => {
        // Early return if no target or ref element
        if (!target || !targetElement) return false;

        // Type guard to check if target is a Node
        const isNode = (obj: EventTarget | null): obj is Node => {
          return obj !== null && "nodeType" in obj;
        };

        // Safe contains check
        const safeContains = (
          parent: Node,
          child: EventTarget | null,
        ): boolean => {
          return (
            isNode(child) &&
            (parent === child || (parent.contains && parent.contains(child)))
          );
        };

        // Check if target is the element itself or a descendant
        if (safeContains(targetElement, target)) {
          return true;
        }

        // Check Shadow DOM if it exists
        const shadowRoot = targetElement.shadowRoot;
        if (shadowRoot && safeContains(shadowRoot, target)) {
          return true;
        }

        return false;
      };

      // Traverse up the composed path to check if the click is inside
      const composedPath = event.composedPath();
      const isClickInside = composedPath.some(isInside);

      // Call handler if click is outside
      if (!isClickInside) {
        handler(event);
      }
    },
    [handler],
  );

  useEffect(() => {
    // Add event listeners for both mouse and touch events
    document.addEventListener("mousedown", handleClickOutside, listenCapturing);
    document.addEventListener(
      "touchstart",
      handleClickOutside,
      listenCapturing,
    );

    // Cleanup
    return () => {
      document.removeEventListener(
        "mousedown",
        handleClickOutside,
        listenCapturing,
      );
      document.removeEventListener(
        "touchstart",
        handleClickOutside,
        listenCapturing,
      );
    };
  }, [handleClickOutside, listenCapturing]);

  return ref;
}
